'use strict';

import React from 'react';
import AutoGraph from '@mui/icons-material/AutoGraph';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { navigateToIBP } from '@/utils/auth0Token';
import { DISCOVERY_PANEL_EXPLORE_BODY_TEXT } from '@/features/discoveryPanels/useDiscoveryPanelEntries';
import { PROJECT_TOOL_GROUP, PROJECT_TOOL_IDS } from './projectToolTypes';
import {
	getProjectToolMuiIcon,
	getProjectToolSidebarChildMuiIcon,
	getProjectToolSidebarLucideIcon,
} from './projectToolIcons';

const EXPLORE_BODY_RESULTS = 'Click to download data deliverables for your project.';
const EXPLORE_BODY_PATHWAYS = 'Explore the interactive pathway map.';
const EXPLORE_BODY_IBP = 'Visualize your data in our bioinformatics platform.';

/**
 * Landing Project Tools paper cards (MUI icons, history.push or project picker).
 *
 * @param {import('./projectToolsBuilder').ProjectTool[]} tools
 * @param {{ history: { push: (path: string) => void }; projectCode: string; onOpenProjectPicker: () => void }} opts
 */
export function projectToolsToLandingCards(tools, { history, projectCode, onOpenProjectPicker }) {
	return tools.map((tool) => {
		const icon = getProjectToolMuiIcon(tool);
		if (tool.kind === 'external' && tool.externalHref) {
			return {
				id: tool.id,
				title: tool.label,
				icon,
				action: () => navigateToIBP(tool.externalHref),
			};
		}
		return {
			id: tool.id,
			title: tool.label,
			icon,
			action: () => {
				if (tool.path) {
					history.push(tool.path);
				} else if (tool.landingNeedsProject && !projectCode) {
					onOpenProjectPicker();
				}
			},
		};
	});
}

/**
 * Project sidebar: `items` groups for Navigation (primary + optional secondary IBP).
 *
 * @param {import('./projectToolsBuilder').ProjectTool[]} tools
 */
export function projectToolsToSidebarItems(tools) {
	const primary = tools.filter((t) => t.group === PROJECT_TOOL_GROUP.PRIMARY);
	const secondary = tools.filter((t) => t.group === PROJECT_TOOL_GROUP.SECONDARY);

	const mapPrimary = (tool) => {
		if (tool.kind === 'discovery') {
			return {
				icon: getProjectToolSidebarLucideIcon(tool),
				label: tool.label,
				active: false,
				target: '_self',
				href: tool.path,
				exact: true,
			};
		}
		if (tool.id === PROJECT_TOOL_IDS.HOME) {
			return {
				icon: getProjectToolSidebarLucideIcon(tool),
				label: 'Home',
				active: false,
				target: '_self',
				href: tool.path,
				exact: true,
			};
		}
		if (tool.children && tool.children.length) {
			const ChildIcon0 = getProjectToolSidebarChildMuiIcon(tool.children[0]);
			const ChildIcon1 = getProjectToolSidebarChildMuiIcon(tool.children[1]);
			return {
				icon: getProjectToolSidebarLucideIcon(tool),
				label: tool.label,
				active: false,
				target: '_self',
				href: tool.path,
				children: [
					{
						icon: ChildIcon0,
						label: tool.children[0].label,
						active: false,
						target: '_self',
						href: tool.children[0].path,
					},
					{
						icon: ChildIcon1,
						label: tool.children[1].label,
						active: false,
						target: '_self',
						href: tool.children[1].path,
					},
				],
			};
		}
		return {
			icon: getProjectToolSidebarLucideIcon(tool),
			label: tool.label,
			active: false,
			target: '_self',
			href: tool.path,
		};
	};

	const primaryItems = primary.map(mapPrimary);
	const groups = [{ items: primaryItems }];

	if (secondary.length) {
		groups.push({
			items: secondary.map((tool) => ({
				icon: AutoGraph,
				label: tool.label,
				external: true,
				active: false,
				target: '_self',
				href: tool.externalHref,
				willRender: true,
			})),
		});
	}

	return groups;
}

/**
 * ExploreMetric rows for ProjectHome.
 *
 * @param {import('./projectToolsBuilder').ProjectTool[]} tools
 * @param {{ biovizStatus: boolean }} opts
 */
export function projectToolsToExploreItems(tools, { biovizStatus }) {
	const hasDiscovery = tools.some((t) => t.kind === 'discovery');
	if (hasDiscovery) {
		return tools
			.filter((t) => t.kind === 'discovery' && t.path)
			.map((t) => ({
				icon: React.cloneElement(getProjectToolMuiIcon(t), { fontSize: 'inherit' }),
				label: t.label,
				bodyText: DISCOVERY_PANEL_EXPLORE_BODY_TEXT,
				href: t.path,
				external: false,
				willRender: true,
			}));
	}

	const out = [];
	const pushIf = (id, fn) => {
		const t = tools.find((x) => x.id === id);
		if (t && t.path) {
			fn(t);
		}
	};
	pushIf(PROJECT_TOOL_IDS.RESULTS, (t) => {
		out.push({
			icon: <ContentCopy fontSize="inherit" />,
			label: 'Results',
			bodyText: EXPLORE_BODY_RESULTS,
			href: t.path,
			external: false,
			willRender: true,
		});
	});
	pushIf(PROJECT_TOOL_IDS.PATHWAYS, (t) => {
		out.push({
			icon: React.cloneElement(getProjectToolMuiIcon(t), { fontSize: 'inherit' }),
			label: 'Pathway Explorer',
			bodyText: EXPLORE_BODY_PATHWAYS,
			href: t.path,
			external: false,
			willRender: true,
		});
	});
	const ibp = tools.find((x) => x.id === PROJECT_TOOL_IDS.IBP);
	if (ibp && ibp.externalHref) {
		out.push({
			icon: React.cloneElement(getProjectToolMuiIcon(ibp), { fontSize: 'inherit' }),
			label: 'Bioinformatics Tool',
			bodyText: EXPLORE_BODY_IBP,
			href: ibp.externalHref,
			external: true,
			willRender: biovizStatus,
		});
	}
	return out;
}
